import { useEffect, useRef, useState, type ReactNode } from "react";

type ClampedTextProps = {
  children: ReactNode;
  /** Styles for the text itself (size, color, …). */
  className?: string;
  /** Max-height utility applied while collapsed (below sm). */
  clampClassName?: string;
  moreLabel?: string;
  lessLabel?: string;
};

/**
 * Clamps its content to a max height on mobile (below the `sm` breakpoint) with
 * a "Show more"/"Show less" toggle, and shows the full content with no toggle
 * at `sm` and up. The toggle only appears when the content actually overflows
 * the clamp, so short content stays toggle-free. The toggle stops click
 * propagation, so it is safe inside a clickable parent (cards, options).
 *
 * String children are rendered Markdown-style: a blank line (two+ newlines)
 * starts a new paragraph with spacing, while a single newline is a soft line
 * break that stays tight (so simple lists don't gain paragraph gaps).
 */
export default function ClampedText({
  children,
  className = "",
  clampClassName = "max-h-40",
  moreLabel = "Show more",
  lessLabel = "Show less",
}: ClampedTextProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Measure overflow only while collapsed (the clamp is active then). Once
  // expanded we keep the toggle so the user can collapse again. Re-measures on
  // resize — e.g. crossing the sm breakpoint, where the clamp turns off.
  useEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () =>
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, children]);

  const paragraphs =
    typeof children === "string" ? children.trim().split(/\n{2,}/) : null;

  // `className` (size/color) goes on the wrapper so both the text and the
  // toggle inherit it — the toggle then matches the description's font size
  // and color instead of looking like a separate button.
  return (
    <div className={className}>
      <div
        ref={ref}
        className={
          expanded
            ? undefined
            : `${clampClassName} overflow-hidden sm:max-h-none`
        }
      >
        {paragraphs ? (
          // space-y-* is the inter-paragraph gap (blank line in the source);
          // single newlines stay tight as line breaks within a paragraph.
          <div className="space-y-2.5">
            {paragraphs.map((paragraph, index) => (
              <p key={index} className="whitespace-pre-line">
                {paragraph}
              </p>
            ))}
          </div>
        ) : (
          children
        )}
      </div>
      {isOverflowing && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          className="mt-1 block w-fit p-0 font-medium underline sm:hidden"
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  );
}
